/**
 * @fileoverview Backend appCode to client error mapping.
 * @description Maps the identity backend's public `appCode` (a stable string every failure
 * response carries, e.g. `'1001'`) to the client-side ErrorCode and the i18n key for the generic
 * message shown to the user — see response.interceptor.ts, the only consumer. Deliberately generic
 * per-appCode rather than per internal failure reason: several distinct backend failure reasons
 * (unknown identifier, wrong password, locked/suspended/deactivated account, blocked device, ...)
 * all collapse to the same public appCode/message so the UI never reveals which one occurred.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { ERROR_CODES } from '@/shared/constants/error-codes.constants'

import type { ErrorCode } from '@/shared/constants/error-codes.constants'

export interface AppErrorCodeMapping {
  code: ErrorCode
  messageKey: string
}

export const APP_ERROR_CODE_MAP: Record<string, AppErrorCodeMapping> = {
  '1001': { code: ERROR_CODES.AUTH_INVALID_CREDENTIALS, messageKey: 'errors.appCode.1001' },
  '1002': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1002' },
  '1003': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1003' },
  '1004': { code: ERROR_CODES.MFA_INVALID_CODE, messageKey: 'errors.appCode.1004' },
  '1005': { code: ERROR_CODES.MFA_CHALLENGE_EXPIRED, messageKey: 'errors.appCode.1005' },
  '1006': { code: ERROR_CODES.MFA_MAX_ATTEMPTS_EXCEEDED, messageKey: 'errors.appCode.1006' },
  '1007': { code: ERROR_CODES.MFA_CHALLENGE_INVALID, messageKey: 'errors.appCode.1007' },
  '1008': { code: ERROR_CODES.AUTH_FLOW_EXPIRED, messageKey: 'errors.appCode.1008' },
  '1009': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1009' },
  '1010': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1010' },
  '1011': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1011' },
  '1012': { code: ERROR_CODES.MFA_MAX_RESENDS_EXCEEDED, messageKey: 'errors.appCode.1012' },
  '1013': { code: ERROR_CODES.MFA_CHALLENGE_INVALID, messageKey: 'errors.appCode.1013' },
  '1014': { code: ERROR_CODES.AUTH_FLOW_EXPIRED, messageKey: 'errors.appCode.1014' },
  '1015': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1015' },
  '1016': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1016' },
  '1017': { code: ERROR_CODES.SESSION_EXPIRED, messageKey: 'errors.appCode.1017' },
  '1018': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1018' },
  '1019': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1019' },
  '1020': { code: ERROR_CODES.SESSION_EXPIRED, messageKey: 'errors.appCode.1020' },
  '1021': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1021' },
  '1022': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1022' },
  '1029': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1029' },
  '1030': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1030' },
  '1031': {
    code: ERROR_CODES.PASSWORD_RESET_TOKEN_INVALID,
    messageKey: 'errors.appCode.1031',
  },
  '1032': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1032' },
  '1033': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1033' },
  '1034': { code: ERROR_CODES.PASSWORD_POLICY_REJECTED, messageKey: 'errors.appCode.1034' },
  '1035': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1035' },
  '1036': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1036' },
  '1037': { code: ERROR_CODES.CURRENT_PASSWORD_INCORRECT, messageKey: 'errors.appCode.1037' },
  '1038': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1038' },
  '1039': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1039' },
  '1040': { code: ERROR_CODES.INVITATION_INVALID, messageKey: 'errors.appCode.1040' },
  '1041': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1041' },
  '1042': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1042' },
  '1043': { code: ERROR_CODES.PASSWORD_CHANGE_REQUIRED, messageKey: 'errors.appCode.1043' },
  '1044': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1044' },
  '1045': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1045' },
  '1046': { code: ERROR_CODES.MFA_CHALLENGE_INVALID, messageKey: 'errors.appCode.1046' },
  '1047': { code: ERROR_CODES.MFA_INVALID_CODE, messageKey: 'errors.appCode.1047' },
  '1048': { code: ERROR_CODES.MFA_CHALLENGE_EXPIRED, messageKey: 'errors.appCode.1048' },
  '1049': { code: ERROR_CODES.MFA_MAX_ATTEMPTS_EXCEEDED, messageKey: 'errors.appCode.1049' },
  '1050': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1050' },
  '1051': { code: ERROR_CODES.MFA_MAX_RESENDS_EXCEEDED, messageKey: 'errors.appCode.1051' },
  '1052': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1052' },
  '1053': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1053' },
  '1054': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.1054' },
  '1055': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1055' },
  '1056': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1056' },
  '1057': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.1057' },
  '1058': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.1058' },
  '1059': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1059' },
  '1060': { code: ERROR_CODES.FORBIDDEN, messageKey: 'errors.appCode.1060' },
  '1061': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1061' },
  '1072': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1072' },
  '1073': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1073' },
  '1074': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1074' },
  '1075': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.1075' },
  '1076': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.1076' },
  '1077': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.1077' },
  '2002': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.2002' },
  '2008': { code: ERROR_CODES.FORBIDDEN, messageKey: 'errors.appCode.2008' },
  '2009': { code: ERROR_CODES.STEP_UP_MFA_REQUIRED, messageKey: 'errors.appCode.2009' },
  '2010': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.2010' },
  '2011': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.2011' },
  '2013': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.2013' },
  '2014': { code: ERROR_CODES.PRECONDITION_FAILED, messageKey: 'errors.appCode.2014' },
  '2015': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.2015' },
  '2016': { code: ERROR_CODES.VALIDATION_ERROR, messageKey: 'errors.appCode.2016' },
  '2017': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.2017' },
  '2018': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.2018' },
  '2019': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.2019' },
  '2020': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.2020' },
  '2021': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.2021' },
  '2022': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.2022' },
  '2023': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.2023' },
  '2024': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.2024' },
  '2025': { code: ERROR_CODES.PRECONDITION_FAILED, messageKey: 'errors.appCode.2025' },
  '2026': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.2026' },
  '3001': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.3001' },
  '3010': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.3010' },
  '3011': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.3011' },
  '3012': { code: ERROR_CODES.FORBIDDEN, messageKey: 'errors.appCode.3012' },
  '3017': { code: ERROR_CODES.PRECONDITION_FAILED, messageKey: 'errors.appCode.3017' },
  // A stale If-Match on Update User: the caller refetches the user and lets the editor retry.
  '3021': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.3021' },
  '3022': { code: ERROR_CODES.VERSION_CONFLICT, messageKey: 'errors.appCode.3022' },
  '3023': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.3023' },
  '3024': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.3024' },
  '3026': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.3026' },
  '3027': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.3027' },
  '3028': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.3028' },
  // Deactivate and Reactivate User refusals.
  '3029': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.3029' },
  '3030': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.3030' },
  '3031': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.3031' },
  // Audit read APIs: show these inline on the Audit Log page rather than bouncing to /403 or /500.
  '7001': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.7001' },
  '7004': { code: ERROR_CODES.FORBIDDEN, messageKey: 'errors.appCode.7004' },
  '7005': { code: ERROR_CODES.VALIDATION_ERROR, messageKey: 'errors.appCode.7005' },
  '7006': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.7006' },
  '7007': { code: ERROR_CODES.VALIDATION_ERROR, messageKey: 'errors.appCode.7007' },
  '7008': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.7008' },
  '7009': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.7009' },
  // Sales CRM reads, Sales Performance and Active Clients: show these inline on the page (or in
  // the dialog) rather than bouncing to /403 or /500.
  '4016': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.4016' },
  '4102': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.4102' },
  '4125': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.4125' },
  '4126': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.4126' },
  '4127': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.4127' },
  '4128': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.4128' },
  '4129': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.4129' },
  '4107': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.4107' },
  '4108': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.4108' },
  '4130': { code: ERROR_CODES.FORBIDDEN, messageKey: 'errors.appCode.4130' },
  '4131': { code: ERROR_CODES.NOT_FOUND, messageKey: 'errors.appCode.4131' },
  '4132': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.4132' },
  // Notification preferences: show these inline in the profile's Notifications tab.
  '6003': { code: ERROR_CODES.FORBIDDEN, messageKey: 'errors.appCode.6003' },
  '6006': { code: ERROR_CODES.CONFLICT, messageKey: 'errors.appCode.6006' },
  '6007': { code: ERROR_CODES.VERSION_CONFLICT, messageKey: 'errors.appCode.6007' },
  '6008': { code: ERROR_CODES.SERVICE_UNAVAILABLE, messageKey: 'errors.appCode.6008' },
  '6009': { code: ERROR_CODES.RATE_LIMITED, messageKey: 'errors.appCode.6009' },
  '0001': { code: ERROR_CODES.VALIDATION_ERROR, messageKey: 'errors.appCode.0001' },
  '0005': { code: ERROR_CODES.REQUEST_ORIGIN_REJECTED, messageKey: 'errors.appCode.0005' },
  '0003': { code: ERROR_CODES.PAYLOAD_TOO_LARGE, messageKey: 'errors.appCode.0003' },
  // '0000' (unexpected system failure) is deliberately unmapped — it falls through to the
  // interceptor's own generic fallback message, which already is the platform-standard text.
}
